import { useMutation, useQuery, useQueryClient } from "react-query";
import { shouldNotRetry403Or404 } from "src/config/react-query";

import { ProjectAIProductID } from "src/reusable/products";
import axios from "axios";
import { nuclearn_api_url } from "src/App";

const putProjectFiles = (datasetId, fileBinary) => {
  return axios({
    method: "put",
    url: `${nuclearn_api_url}/products/projectai/upload-project-file`,
    data: fileBinary,
    params: {
      dataset_id: datasetId,
    },
  }).then((response) => {
    return response.data;
  });
};

export const uploadProjectFile = ({ datasetId, projectFile: fileBinary }) => {
  return putProjectFiles(datasetId, fileBinary);
};

export const uploadProjectFiles = (projectsFiles) => {
  // iterate the project files and get the datasetId and fileBinary
  projectsFiles.forEach((projectFile) => {
    const { datasetId } = projectFile;
    const fileBinary = projectFile.projectFile;
    return putProjectFiles(datasetId, fileBinary);
  });
};
export const getSuccessorsPredecessors = (
  datasetId,
  fileBinaryName,
  taskId,
) => {
  return axios({
    method: "get",
    url: `${nuclearn_api_url}/products/projectai/get-successor-and-predecessor/${taskId}/${fileBinaryName}/${datasetId}`,
  }).then((response) => {
    return response.data;
  });
};
export const updateBaselineToActualTies = ({
  artifactId,
  sdrId,
  mappingKey,
  mappingValue,
  baseline_sdr,
  actual_sdr,
}) => {
  let payload = {
    artifact_id: artifactId,
    source_data_record_id: sdrId,
    mapping_key: mappingKey,
    mapping_value: mappingValue,
    baseline_sdr: baseline_sdr ?? null,
    actual_sdr: actual_sdr ?? null,
  };

  return axios({
    method: "POST",
    url: `${nuclearn_api_url}/products/projectai/update-sdr-baseline-actual-ties`,
    data: payload,
    params: payload,
  }).then((response) => {
    return response.data;
  });
};

export const useMappingUpdate = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation((data) => updateBaselineToActualTies(data), {
    onSuccess: () => {
      queryClient.invalidateQueries("allSourceDataRecords");
      queryClient.invalidateQueries("allArtifactsByDatasetId");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

export const softDeleteProjectFile = (artifact_id) => {
  return axios({
    method: "delete",
    url: `${nuclearn_api_url}/products/projectai/soft-delete-project-file-by-artifact-id/${artifact_id}`,
  }).then((response) => {
    return response.data;
  });
};

export const useSoftDeleteProjectFile = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(softDeleteProjectFile, {
    onSuccess: () => {
      queryClient.invalidateQueries("allSourceDataRecords");
      queryClient.invalidateQueries("allArtifactsByDatasetId");
    },
    onError: (error) => {
      console.error(error.response);
    },
  });
  return mutation;
};

export const permanentlyDeleteProjectFile = (artifact_id) => {
  return axios({
    method: "delete",
    url: `${nuclearn_api_url}/products/projectai/delete-project-file/${artifact_id}`,
  }).then((response) => {
    return response.data;
  });
};

const readAnalyticsData = async ({
  datasetId,
  baselineProjects,
  keyActivity,
  modelVersionId,
  downloadCsvAfterLoading = false,
  simulationParams,
  project_id,
}) => {
  console.log("project_id", project_id);
  try {
    const response = await axios.post(
      `${nuclearn_api_url}/products/projectai/analyze`,
      {
        dataset_id: datasetId,
        baseline_projects: baselineProjects,
        model_version_id: modelVersionId,
        key_activity: keyActivity,
        n_sims: simulationParams.simulations_count ?? 100,
        msoa_mode: simulationParams.msoa_mode ?? "none",
        download_csv: downloadCsvAfterLoading,
        project_id: parseInt(project_id, 10),
        name: null,
        description: null,
      },
      {
        params: {
          dataset_id: datasetId,
          baseline_projects: baselineProjects,
          model_version_id: modelVersionId,
          key_activity: keyActivity,
          n_sims: simulationParams.simulations_count,
          msoa_mode: simulationParams.msoa_mode,
          download_csv: downloadCsvAfterLoading,
          project_id: parseInt(project_id, 10), // Added radix parameter
          name: null,
          description: null,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error loading analytic", error);
    throw error;
  }
};

export const useReadAnalyticsData = (analyticsParams) => {
  const queryClient = useQueryClient();
  return useQuery(
    ["analytics", analyticsParams.project_id],
    () => readAnalyticsData(analyticsParams),
    {
      keepPreviousData: true,
      enabled: false,
      onSuccess: () => {
        queryClient.invalidateQueries("latestAnalysisResults");
        queryClient.invalidateQueries("latestProjectAnalysis");
        queryClient.invalidateQueries("projectList");
        queryClient.invalidateQueries("latestProjectAnalysis");
        queryClient.invalidateQueries("portfolioProjectsByRisk");
        queryClient.invalidateQueries("portfolioProjectsByRiskAndDueDate");
        queryClient.invalidateQueries("insightList");
        queryClient.invalidateQueries("inactiveInsightList");
        queryClient.invalidateQueries("mappedDashboardInsights");
      },
      onError: (error) => {
        // Handle errors here, you can log them or show an error message.
        return error;
      },
    },
  );
};

const getModelTrainingStatus = (modelId, datasetId) => {
  return axios
    .get(`${nuclearn_api_url}/products/projectai/model-training/status`, {
      params: {
        model_id: modelId,
        dataset_id: datasetId,
      },
    })
    .then((res) => res.data);
};

export const useGetModelTrainingStatus = (modelId, datasetId) => {
  const queryClient = useQueryClient();

  return useQuery(
    ["projectAiModelTrainingStatus", [modelId, datasetId]],
    () => getModelTrainingStatus(modelId, datasetId),
    {
      refetchOnWindowFocus: false,
      enabled: !!modelId && !!datasetId,
      retry: true, // shouldNotRetry404,
      refetchInterval: () => 2500,
      onSuccess: (data) => {
        if (data?.pct_complete === 1) {
          queryClient.invalidateQueries(["modelVersions", modelId]); // 5 is product id for ProjectAI, refetches models in ProjectSelector.js
          queryClient.invalidateQueries(["getModels", ProjectAIProductID]); // 5 is product id for ProjectAI, refetches models in ProjectSelector.js
          queryClient.invalidateQueries(["datasetModels"]); // refetches models on training modal
        }
      },
    },
  );
};

export const downloadAnalyzeReport = (artifactId) => {
  axios
    .get(
      `${nuclearn_api_url}/products/projectai/download-analytic-csv/${artifactId}`,
      {
        responseType: "arraybuffer",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/csv",
        },
      },
    )
    .then((resp) => {
      let blob = new Blob([resp.data], {
        type: "text/csv",
      });

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `Nuclearn-Project-Genius-${new Date().getUTCDate()}.csv`;
      link.click();
    })
    .catch((e) => {
      console.error(e);
    });
};

export const downloadInsightResultsCSV = (insight) => {
  axios
    .post(
      `${nuclearn_api_url}/artifacts/download-json-list-as-csv`,
      {
        rows: insight?.results,
      },
      {
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/csv",
        },
      },
    )
    .then((resp) => {
      let blob = new Blob([resp.data], {
        type: "text/csv",
      });

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `${insight.name}-${new Date().toISOString()}.csv`; // Updated file name
      link.click();
    })
    .catch((e) => {
      console.error(e);
    });
};

export const downloadAllTasksResultsCSV = (allTasks) => {
  axios
    .post(
      `${nuclearn_api_url}/artifacts/download-json-list-as-csv`,
      {
        rows: allTasks?.all_tasks,
      },
      {
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/csv",
        },
      },
    )
    .then((resp) => {
      let blob = new Blob([resp.data], {
        type: "text/csv",
      });

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `all-tasks-${new Date().toISOString()}.csv`; // Updated file name
      link.click();
    })
    .catch((e) => {
      console.error(e);
    });
};

const createProject = ({ projectName, dataset }) => {
  // test
  return axios
    .put(nuclearn_api_url + `/products/projectai/create-project`, {
      name: projectName,
      dataset_fk: dataset || null,
    })
    .then((response) => {
      return response.data;
    });
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation((data) => createProject(data), {
    onSuccess: () => {
      queryClient.invalidateQueries("project");
      queryClient.invalidateQueries("projectList");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const createInsight = ({
  name,
  sortfilter,
  col_order,
  is_public,
  dashboard_fk,
}) => {
  // Check if necessary params are invalid
  if (
    !name ||
    !sortfilter ||
    is_public === undefined ||
    is_public === null ||
    dashboard_fk === undefined ||
    dashboard_fk === null
  ) {
    return;
  }

  const requestData = {
    name,
    sortfilter,
    col_order,
    is_public,
    dashboard_fk,
  };

  return axios
    .put(nuclearn_api_url + `/products/projectai/create-insight`, requestData)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      console.log(error.response.data);
      throw new Error(error.response.data.detail);
    });
};

export const useCreateInsight = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation((insightData) => createInsight(insightData), {
    onSuccess: () => {
      queryClient.invalidateQueries("insightList");
      queryClient.invalidateQueries("inactiveInsightList");
      queryClient.invalidateQueries("mappedDashboardInsights");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const updateInsight = (insightId, insightData) => {
  return axios
    .patch(
      nuclearn_api_url + `/products/projectai/update-insight/${insightId}`,
      insightData.data,
    )
    .then((res) => res.data)
    .catch((error) => {
      throw new Error(error.response.data.detail);
    });
};

export const useUpdateInsight = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ insightId, ...data }) => updateInsight(insightId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("insightList");
        queryClient.invalidateQueries("inactiveInsightList");
        queryClient.invalidateQueries("mappedDashboardInsights");
        queryClient.invalidateQueries("latestAnalysisResults");
      },
      onError: (error) => {
        console.log(error.response);
      },
    },
  );
};

const getProjectList = () => {
  return axios
    .get(nuclearn_api_url + `/products/projectai/projects`)
    .then((res) => {
      return res.data;
    });
};

export const useGetProjectList = () => {
  return useQuery(["projectList"], () => getProjectList(), {
    refetchOnWindowFocus: false,
    retry: shouldNotRetry403Or404,
  });
};

const getSortFilterFields = (projectId) => {
  return axios
    .get(
      nuclearn_api_url +
        `/products/projectai/get-sortfilter-fields/${projectId}`,
    )
    .then((res) => {
      return res.data;
    });
};

export const useGetSortFilterFields = (projectId) => {
  return useQuery(["sortfilterFields"], () => getSortFilterFields(projectId), {
    refetchOnWindowFocus: false,
    enabled: projectId !== null && projectId !== undefined,
  });
};

const getDashboard = () => {
  return axios
    .get(nuclearn_api_url + `/products/projectai/get-dashboard`)
    .then((res) => {
      return res.data;
    });
};

export const useGetDashboard = () => {
  return useQuery(["dashboard"], () => getDashboard(), {
    refetchOnWindowFocus: false,
  });
};

const deleteInsight = (insightId) => {
  return axios
    .delete(
      nuclearn_api_url + "/products/projectai/delete-insight/" + insightId,
    )
    .then(() => {
      return insightId;
    });
};

export const useDeleteInsight = (insightId, removeInsightFromItems) => {
  const queryClient = useQueryClient();

  return useMutation(() => deleteInsight(insightId), {
    onSuccess: () => {
      removeInsightFromItems(insightId);
      queryClient.invalidateQueries("insightList");
      queryClient.invalidateQueries("inactiveInsightList");
      queryClient.invalidateQueries("mappedDashboardInsights");
    },
    enabled: Boolean(insightId),
  });
};

const getInsightById = (insightId) => {
  return axios
    .get(nuclearn_api_url + `/products/projectai/get-insight/${insightId}`)
    .then((res) => res.data);
};

export const useGetInsightById = (insightId) => {
  return useQuery(["insight", insightId], () => getInsightById(insightId), {
    refetchOnWindowFocus: false,
    enabled: !!insightId,
  });
};

const associateInsightToDashboard = ({ insightId, dashboardId, layout }) => {
  return axios
    .post(
      `${nuclearn_api_url}/products/projectai/associate-insight-with-dashboard`,
      {
        insight_id: insightId,
        dashboard_id: dashboardId,
        layout: layout,
      },
    )
    .then((res) => res.data);
};

export const useAssociateInsightToDashboard = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(associateInsightToDashboard, {
    onSuccess: () => {
      queryClient.invalidateQueries("insightList");
      queryClient.invalidateQueries("inactiveInsightList");
      queryClient.invalidateQueries("mappedDashboardInsights");
      queryClient.invalidateQueries("latestAnalysisResults");
      queryClient.invalidateQueries("insightsFromAnalysis");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const disassociateInsightFromDashboard = ({ insightId, dashboardId }) => {
  return axios
    .delete(
      `${nuclearn_api_url}/products/projectai/delete-insight-dashboard-tie/${insightId}/${dashboardId}`,
    )
    .then((res) => res.data);
};

export const useDisassociateInsightFromDashboard = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(disassociateInsightFromDashboard, {
    onSuccess: () => {
      queryClient.invalidateQueries("insightList");
      queryClient.invalidateQueries("inactiveInsightList");
      queryClient.invalidateQueries("mappedDashboardInsights");
      queryClient.invalidateQueries("latestAnalysisResults");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const getLatestProjectAnalysis = (project_id) => {
  return axios
    .get(
      `${nuclearn_api_url}/products/projectai/get-latest-project-analysis/` +
        project_id,
    )
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      console.error("There was an error!", error);
      throw error;
    });
};

export const useGetLatestProjectAnalysis = (project_id) => {
  return useQuery(
    ["latestProjectAnalysis"],
    () => getLatestProjectAnalysis(project_id),
    {
      refetchOnWindowFocus: false,
      enabled: !!project_id,
    },
  );
};

export const getLatestAnalysisResults = (id, keyActivity) => {
  if (id === null || id === undefined) {
    return Promise.reject(new Error("Invalid ID"));
  }
  return axios
    .get(
      `${nuclearn_api_url}/products/projectai/get-latest-analysis-results/` +
        id +
        `?key_activity=${keyActivity === null ? "" : keyActivity}`,
    )
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      // Handle error. Adjust this as per your requirements.
      console.error("There was an error!", error);
      throw error;
    });
};

export const useGetLatestAnalysisResults = (
  id,
  keyActivity = null,
  projectAnalysisSuccess = false,
) => {
  const queryKey = ["latestAnalysisResults", id, keyActivity];
  const isEnabled = id !== null && id !== undefined && projectAnalysisSuccess;
  return useQuery(queryKey, () => getLatestAnalysisResults(id, keyActivity), {
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: isEnabled,
    retry: false,
  });
};

export const getInsightsFromLatestAnalysis = (id, keyActivity) => {
  return axios
    .get(
      `${nuclearn_api_url}/products/projectai/get-insight-data-from-latest-project-analysis/` +
        id +
        `?key_activity=${keyActivity == null ? "" : keyActivity}`,
    )
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      console.error("There was an error!", error);
      throw error;
    });
};

const getDashboardInsightsMappedToDashboard = (dashboardId, page, limit) => {
  return axios
    .get(
      `${nuclearn_api_url}/products/projectai/get-dashboard-insights/${dashboardId}`,
      {
        params: { limit, offset: (page - 1) * limit },
      },
    )
    .then((res) => res.data)
    .catch((error) => {
      console.error("There was an error!", error);
    });
};

export const useGetDashboardInsightsMappedToDashboard = (
  dashboardId,
  page,
  limit,
) => {
  return useQuery(
    ["mappedDashboardInsights", dashboardId, page, limit],
    () => getDashboardInsightsMappedToDashboard(dashboardId, page, limit),
    { refetchOnWindowFocus: false, enabled: !!dashboardId },
  );
};

const getInsightListNotMappedToDashboard = (page, limit) => {
  return axios
    .get(
      `${nuclearn_api_url}/products/projectai/get-insights/not-in-dashboard`,
      {
        params: { limit, offset: (page - 1) * limit },
      },
    )
    .then((res) => res.data)
    .catch((error) => {
      console.error("There was an error!", error);
    });
};

export const useGetInsightListNotMappedToDashboard = (page, limit) => {
  return useQuery(
    ["inactiveInsightList", page, limit],
    () => getInsightListNotMappedToDashboard(page, limit),
    { refetchOnWindowFocus: false },
  );
};

const getProjectById = (projectId) => {
  return axios
    .get(nuclearn_api_url + `/products/projectai/get-project/${projectId}`)
    .then((res) => res.data);
};

export const useGetProjectById = (projectId) => {
  return useQuery(["project", projectId], () => getProjectById(projectId), {
    refetchOnWindowFocus: false,
    enabled: !!projectId,
  });
};

const getPortfolioProjectsByRisk = () => {
  return axios
    .get(nuclearn_api_url + `/products/projectai/portfolio-projects-by-risk`)
    .then((res) => res.data);
};

export const useGetPortfolioProjectsByRisk = () => {
  return useQuery(
    ["portfolioProjectsByRisk"],
    () => getPortfolioProjectsByRisk(),
    {
      refetchOnWindowFocus: false,
      retry: shouldNotRetry403Or404,
    },
  );
};

const getPortfolioProjectsByRiskAndDueDate = () => {
  return axios
    .get(
      nuclearn_api_url +
        `/products/projectai/portfolio-projects-by-risk-and-target-date`,
    )
    .then((res) => res.data);
};

export const useGetPortfolioProjectsByRiskByDueDate = () => {
  return useQuery(
    ["portfolioProjectsByRiskAndDueDate"],
    () => getPortfolioProjectsByRiskAndDueDate(),
    {
      refetchOnWindowFocus: false,
      retry: shouldNotRetry403Or404,
    },
  );
};

const getThresholds = () => {
  return axios
    .get(nuclearn_api_url + `/products/projectai/threshold-config`)
    .then((res) => res.data);
};

export const useGetThresholds = () => {
  return useQuery(["thresholds"], () => getThresholds(), {
    refetchOnWindowFocus: false,
  });
};

const getSortFiltersByInsightId = (insightId) => {
  return axios
    .get(
      nuclearn_api_url +
        `/products/projectai/get-sort-filters-by-insight-id/${insightId}`,
    )
    .then((res) => res.data);
};

export const useGetSortFiltersByInsightId = (insightId) => {
  return useQuery(
    ["sortFiltersByInsightId", insightId],
    () => getSortFiltersByInsightId(insightId),
    {
      refetchOnWindowFocus: false,
      enabled: !!insightId,
    },
  );
};

export const setKeyActivity = ({ project_id, keyActivity }) => {
  return axios
    .post(`${nuclearn_api_url}/products/projectai/set-key-activity`, {
      project_id: project_id,
      key_activity: keyActivity,
    })
    .then((res) => res.data);
};

export const useSetKeyActivity = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(setKeyActivity, {
    onSuccess: () => {
      queryClient.invalidateQueries("latestProjectAnalysis");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

export const setProjectTargetEndDate = ({ project_id, selectedDate }) => {
  return axios
    .post(
      `${nuclearn_api_url}/products/projectai/set-target-end-date-for-project`,
      {
        project_id: project_id,
        target_end_date: selectedDate,
      },
    )
    .then((res) => res.data);
};

export const useSetProjectTargetEndDate = (projectId) => {
  const queryClient = useQueryClient();

  const mutation = useMutation(setProjectTargetEndDate, {
    onSuccess: () => {
      queryClient.invalidateQueries("latestAnalysisResults");
      queryClient.invalidateQueries(["project", projectId]);
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const deletePermanentlyProject = (projects) => {
  return axios.delete(
    nuclearn_api_url + "/products/projectai/permanent-delete-projects-by-ids",
    { data: projects.map((project) => project.id) },
  );
};

export const useDeletePermanentlyProject = () => {
  const queryClient = useQueryClient();

  return useMutation(deletePermanentlyProject, {
    onSuccess: () => {
      queryClient.invalidateQueries("projectList");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
};

const deleteProjectByID = (projectId) => {
  return axios
    .delete(
      nuclearn_api_url +
        "/products/projectai/delete-project-by-id/" +
        projectId,
    )
    .then((res) => res.data);
};

export const useDeleteProjectByID = (projectId) => {
  const queryClient = useQueryClient();

  return useMutation(() => deleteProjectByID(projectId), {
    onSuccess: () => {
      queryClient.invalidateQueries("projectList");
      queryClient.invalidateQueries("portfolioProjectsByRiskAndDueDate");
      queryClient.invalidateQueries("portfolioProjectsByRisk");
    },
  });
};
